import React, { useState, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Users, Activity, BarChart3, Megaphone, Settings,
  Hexagon, ChevronLeft, LogOut, ShieldCheck, Menu, X, ShoppingBag, Tag, Gift, Package, Swords, ClipboardCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api`;

// Override AuthContext for admin pages so useAuth() returns admin credentials
const AdminAuthOverride = createContext(null);

export function useAdminAuth() {
  return useContext(AdminAuthOverride);
}

function AdminAuthProvider({ children }) {
  const token = localStorage.getItem('hp_admin_token');
  const user = JSON.parse(localStorage.getItem('hp_admin_user') || 'null');
  const refreshUser = async () => {};
  const logout = () => {
    localStorage.removeItem('hp_admin_token');
    localStorage.removeItem('hp_admin_user');
  };
  return (
    <AdminAuthOverride.Provider value={{ token, user, API: ADMIN_API, refreshUser, logout }}>
      {children}
    </AdminAuthOverride.Provider>
  );
}

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/ayo-panel' },
  { id: 'users', label: 'Users', icon: Users, path: '/ayo-panel/users' },
  { id: 'products', label: 'Products', icon: ShoppingBag, path: '/ayo-panel/products' },
  { id: 'codes', label: 'Creator Codes', icon: Tag, path: '/ayo-panel/codes' },
  { id: 'rewards', label: 'Rewards', icon: Gift, path: '/ayo-panel/rewards' },
  { id: 'redemptions', label: 'Redemptions', icon: Package, path: '/ayo-panel/redemptions' },
  { id: 'quests', label: 'Quests', icon: Swords, path: '/ayo-panel/quests' },
  { id: 'approvals', label: 'Approvals', icon: ClipboardCheck, path: '/ayo-panel/approvals' },
  { id: 'activity', label: 'Activity Feed', icon: Activity, path: '/ayo-panel/activity' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/ayo-panel/analytics' },
  { id: 'announcements', label: 'Announcements', icon: Megaphone, path: '/ayo-panel/announcements' },
  { id: 'config', label: 'Configuration', icon: Settings, path: '/ayo-panel/config' },
];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  const adminUser = JSON.parse(localStorage.getItem('hp_admin_user') || 'null');
  
  const handleLogout = () => {
    localStorage.removeItem('hp_admin_token');
    localStorage.removeItem('hp_admin_user');
    navigate('/ayo-login');
  };

  const currentPath = location.pathname;

  return (
    <AdminAuthProvider>
    <div className="min-h-screen bg-[#09090B] flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} flex-shrink-0 border-r border-white/5 bg-[#0C0C0E] flex flex-col transition-all duration-300`}>
        {/* Logo */}
        <div className="h-16 border-b border-white/5 flex items-center px-4 gap-3">
          <div className="relative flex-shrink-0">
            <Hexagon className="w-8 h-8 text-amber-500 fill-amber-500/20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2 h-2 bg-amber-500 rounded-full" />
            </div>
          </div>
          {sidebarOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="overflow-hidden">
              <span className="font-heading font-black text-base text-white whitespace-nowrap">HoneyPot</span>
              <span className="block text-[10px] text-amber-500 font-medium -mt-0.5">ADMIN</span>
            </motion.div>
          )}
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {NAV_ITEMS.map((item) => {
            const isActive = currentPath === item.path || (item.path !== '/ayo-panel' && currentPath.startsWith(item.path));
            const isExactDashboard = item.path === '/ayo-panel' && currentPath === '/ayo-panel';
            const active = item.path === '/admin' ? isExactDashboard : isActive;

            return (
              <button
                key={item.id}
                data-testid={`admin-nav-${item.id}`}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                  active
                    ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.03] border border-transparent'
                }`}
              >
                <item.icon className={`w-4.5 h-4.5 flex-shrink-0 ${active ? 'text-amber-500' : ''}`} />
                {sidebarOpen && <span className="truncate">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-white/5 p-3 space-y-2">
          <button
            data-testid="admin-toggle-sidebar"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.03] transition-colors"
          >
            {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            {sidebarOpen && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 border-b border-white/5 bg-[#0C0C0E]/50 backdrop-blur-lg flex items-center justify-between px-8">
          <div>
            <h1 className="font-heading font-bold text-lg text-white">
              {NAV_ITEMS.find(n => {
                if (n.path === '/ayo-panel') return currentPath === '/ayo-panel';
                return currentPath.startsWith(n.path);
              })?.label || 'Admin'}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="hex-clip w-8 h-8 bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-black" />
              </div>
              <div className="text-right">
                <p className="text-xs font-medium text-white">{adminUser?.name}</p>
                <p className="text-[10px] text-zinc-500">{adminUser?.email}</p>
              </div>
            </div>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/5 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto p-8">
          <motion.div
            key={currentPath}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {children}
          </motion.div>
        </div>
      </main>
    </div>
    </AdminAuthProvider>
  );
}
